import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { t, locale } from '../i18n';
import { Card, Rating, SectionTitle, Spinner, EmptyState, Modal, Input, Button } from '../ui';
import ReviewCard from '../components/ReviewCard';

const TRANSLATE_ENDPOINT = 'https://park4night.com/services/V4.1/commGetTrad_cors.php';

/** Modale de traduction d'un commentaire (service V4.1 existant). */
function TranslateModal({ review, onClose }) {
    const [translation, setTranslation] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!review) return;
        setTranslation(null);
        setFailed(false);
        const url = `${TRANSLATE_ENDPOINT}?id_comm=${review.id}&context_lang=${locale}`;
        fetch(url)
            .then((r) => r.json())
            .then((data) => {
                // Le service V4.1 renvoie { status, translation, langue } à plat,
                // et répond toujours en HTTP 200 (même en erreur) : fetch ne rejette
                // donc pas, il faut tester status explicitement. (NB : avec fetch,
                // r.json() = le corps ; pas d'enveloppe .data comme avec axios.)
                if (data && data.status === 'OK' && data.translation) {
                    setTranslation(data.translation);
                } else {
                    setFailed(true);
                }
            })
            .catch(() => setFailed(true));
    }, [review]);

    return (
        <Modal open={!!review} onClose={onClose}>
            <SectionTitle>{t('traduire')}</SectionTitle>
            {review && translation === null && !failed && <Spinner />}
            {failed && <EmptyState>{t('error_update')}</EmptyState>}
            {translation && review && (
                <ReviewCard review={{ ...review, commentaire: translation }} />
            )}
            <div className="toolbar" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
                <Button variant="secondary" onClick={onClose}>{t('fermer')}</Button>
            </div>
        </Modal>
    );
}

/** Barre de répartition des notes, cliquable pour filtrer. */
function NoteFilter({ reviews, active, onSelect }) {
    const counts = useMemo(() => {
        const result = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        reviews.forEach((r) => {
            const note = Math.round(r.note || 0);
            if (result[note] !== undefined) result[note] += 1;
        });
        return result;
    }, [reviews]);

    return (
        <div>
            {[5, 4, 3, 2, 1].map((note) => {
                const percent = reviews.length ? Math.round((counts[note] / reviews.length) * 100) : 0;
                return (
                    <button
                        key={note}
                        type="button"
                        onClick={() => onSelect(active === note ? null : note)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                            border: 'none', background: active === note ? 'var(--blue-50)' : 'none',
                            borderRadius: 'var(--radius-sm)', padding: '6px 8px', cursor: 'pointer',
                        }}
                    >
                        <span className="text-sm" style={{ width: 32, textAlign: 'left', fontWeight: 700 }}>
                            {note} ★
                        </span>
                        <span style={{ flex: 1, height: 8, background: 'var(--bg-3)', borderRadius: 999 }}>
                            <span style={{
                                display: 'block', height: '100%', width: `${percent}%`,
                                background: 'var(--green-400)', borderRadius: 999,
                            }} />
                        </span>
                        <span className="text-muted text-sm" style={{ width: 36, textAlign: 'right' }}>
                            {counts[note]}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

export default function Reviews() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [search, setSearch] = useState('');
    const [noteFilter, setNoteFilter] = useState(null);
    const [translating, setTranslating] = useState(null);

    useEffect(() => {
        api.reviews(id).then(setData).catch(() => setData({ place: null, reviews: [] }));
    }, [id]);

    if (!data) return <Spinner />;
    if (!data.place) return <EmptyState>404</EmptyState>;

    const filtered = data.reviews.filter((review) => {
        if (noteFilter !== null && Math.round(review.note || 0) !== noteFilter) return false;
        if (search) {
            const haystack = `${review.owner ? review.owner.username : ''} ${review.commentaire || ''}`.toLowerCase();
            if (!haystack.includes(search.toLowerCase())) return false;
        }
        return true;
    });

    return (
        <>
            <div className="topbar" style={{ marginBottom: 16 }}>
                <div>
                    <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {data.place.type_code && (
                            <img src={`/img/access/icon_${data.place.type_code.toLowerCase()}.svg`} alt="" style={{ width: 36 }} />
                        )}
                        {data.place.title}
                    </h1>
                    <p className="page-subtitle">
                        <Link to="/" style={{ color: 'var(--p4n-blue)' }}>← {t('retour')}</Link>
                    </p>
                </div>
                <Link to={`/edit/${data.place.id}`} className="btn btn--primary">{t('modifier')}</Link>
            </div>
            <hr className="dashed-divider" />

            <div className="reviews-layout">
                <Card className="reviews-layout__filters">
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                        <div className="stat-value" style={{ fontSize: 'var(--text-3xl)' }}>
                            {data.place.note_moyenne != null ? data.place.note_moyenne.toFixed(1) : '—'}
                        </div>
                        <Rating value={data.place.note_moyenne} />
                        <p className="text-muted text-sm">
                            {data.place.nb_commentaires} {t('mes_commentaires')}
                        </p>
                    </div>
                    <Input placeholder={t('search')} value={search} onChange={(e) => setSearch(e.target.value)} />
                    <div style={{ marginTop: 16 }}>
                        <NoteFilter reviews={data.reviews} active={noteFilter} onSelect={setNoteFilter} />
                    </div>
                    {(noteFilter !== null || search) && (
                        <Button variant="ghost" size="sm" block onClick={() => { setNoteFilter(null); setSearch(''); }}>
                            {t('reinitialiser')}
                        </Button>
                    )}
                </Card>

                <Card>
                    <SectionTitle>{t('mes_commentaires')}</SectionTitle>
                    {filtered.length === 0 && <EmptyState>{t('no_comment')}</EmptyState>}
                    {filtered.map((review) => (
                        <ReviewCard
                            key={review.id}
                            review={review}
                            action={(
                                <Button variant="ghost" size="sm" onClick={() => setTranslating(review)}>
                                    {t('traduire')}
                                </Button>
                            )}
                        />
                    ))}
                </Card>
            </div>

            <TranslateModal review={translating} onClose={() => setTranslating(null)} />
        </>
    );
}
